/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { DocumentStore } from "./DocumentStore";
import { PagedViewState } from "./PagedViewState";
import { DocumentViewStore } from "./DocumentViewStore";
import { Zoom } from "./Zoom";
import { LHPStore } from "./LHPStore";
import { SearchStore } from "./SearchStore";
import { AccessibilityStore } from "./AccessibilityStore";
import { useStrict } from "mobx";
import { OverlayDropinStore } from "./OverlayDropinStore";
import { HUDStore } from "./HUDStore";
import { AppUIState } from "./AppUIState";
import { ContextMenuStore } from "./ContextMenuStore";
import { DCAPIStore } from "./DCAPIStore";
import { PreviewStore } from "./PreviewStore";
import { BookmarkStore } from "./BookmarkStore";
import { DigSigStore } from "./DigSigStore";
import CommonAppStore from "dc-preview-utils/src/stores/CommonAppStore";
import TouchSelStore from "dc-preview-utils/src/stores/TouchSelStore";
import PageHighlightsStore from "dc-preview-utils/src/stores/PageHighlightsStore";
import CursorStore from "dc-preview-utils/src/stores/CursorStore";
import { TopbarStore } from "./TopbarStore";
import { AppStore } from "./AppStore";
import { ExternalAPIStore } from "./ExternalAPIStore";
import { ThumbnailStore } from "./ThumbnailStore";
import { QuickToolsStore } from "./QuickToolsStore";
import { StickyToolsStore } from "./StickyToolsStore";
import { NotificationViewStore } from "./NotificationViewStore";
import { LiquidModeViewStore } from "./LiquidModeViewStore";
import { LiquidModePersonalisationStore } from "./LiquidModePersonalisationStore";
import { commonCodeApis } from "api/commonCodeApis";
import { MIPStore } from "./MIPStore";
import { HighContrastStore } from "./HighContrastStore";
import { PageThemeSwitcherStore } from "./PageThemeSwitcherStore";
import { ReadAloudStore } from "./ReadAloudStore";
import { SkinnyRailStore } from "./SkinnyRailStore";
import { JSStore } from "./JSStore";
import { CaretModeStore } from "./CaretModeStore";
import { PageByPageStore } from "./PageByPageStore";
import { OrganizeStore } from "./OrganizeStore";
import { BottomBoardStore } from "./BottomBoard";
import { FABStore as FabStore } from "./FABStore";
import { TopPanelStore } from "./TopPanelStore";

useStrict(true);

/* Changing let to var below. Earlier we had Transpilation for older browsers which changed let to var before,
 * the variables were used. And when these variables were accessed before initialization, it gave undefined.
 * However with webpack-config 7.0.1, this transpilation has been removed.
 * Thus, when we access these variables, before initialization, it throws an Error.
 * For more details, please read -
 * https://stackoverflow.com/questions/33198849/what-is-the-temporal-dead-zone.
 *
 * Ideally, this code should not have been accessed before initialization.
 * TODO : Review so that code is not accessed before initialization and change var back to let.
 * TODO JIRA : https://jira.corp.adobe.com/browse/ADCS-35120
 */
var documentStore,
  pagedViewState,
  zoom,
  lhpStore,
  documentViewStore,
  searchStore,
  hudStore,
  accessibilityStore,
  overlayDropinStore,
  appUiState,
  contextMenuStore,
  dcapiStore,
  previewStore,
  bookmarkStore,
  digSigStore,
  topbarStore,
  appStore,
  externalStore,
  thumbnailStore,
  commonAppStore,
  touchSelStore,
  pageHighlightsStore,
  quickToolsStore,
  stickyToolsStore,
  notificationViewStore,
  liquidModeViewStore,
  liquidModePersonalisationStore,
  mipStore,
  highContrastStore,
  pageThemeSwitcherStore,
  readAloudStore,
  skinnyRailStore,
  jsStore,
  caretModeStore,
  cursorStore,
  pageByPageStore,
  organizeStore,
  bottomBoard,
  FABStore,
  topPanelStore;

const createStore = () => {
  documentStore = new DocumentStore();
  pagedViewState = new PagedViewState();
  zoom = new Zoom();
  lhpStore = new LHPStore();
  documentViewStore = new DocumentViewStore();
  searchStore = new SearchStore();
  hudStore = new HUDStore();
  accessibilityStore = new AccessibilityStore();
  overlayDropinStore = new OverlayDropinStore();
  appUiState = new AppUIState();
  contextMenuStore = new ContextMenuStore();
  dcapiStore = new DCAPIStore();
  previewStore = new PreviewStore();
  bookmarkStore = new BookmarkStore();
  digSigStore = new DigSigStore();
  topbarStore = new TopbarStore();
  appStore = new AppStore();
  externalStore = new ExternalAPIStore();
  thumbnailStore = new ThumbnailStore();
  commonAppStore = new CommonAppStore();
  touchSelStore = new TouchSelStore();
  pageHighlightsStore = new PageHighlightsStore();
  quickToolsStore = new QuickToolsStore();
  stickyToolsStore = new StickyToolsStore();
  notificationViewStore = new NotificationViewStore();
  liquidModeViewStore = new LiquidModeViewStore();
  liquidModePersonalisationStore = new LiquidModePersonalisationStore();
  mipStore = new MIPStore();
  highContrastStore = new HighContrastStore();
  pageThemeSwitcherStore = new PageThemeSwitcherStore();
  readAloudStore = new ReadAloudStore();
  skinnyRailStore = new SkinnyRailStore();
  jsStore = new JSStore();
  caretModeStore = new CaretModeStore();
  cursorStore = new CursorStore();
  pageByPageStore = new PageByPageStore();
  organizeStore = new OrganizeStore();
  bottomBoard = new BottomBoardStore();
  FABStore = new FabStore();
  topPanelStore = new TopPanelStore();
};

createStore();

export const initStore = (docApi, stores) => {
  documentStore.initStore(docApi, stores);
  pagedViewState.initStore(docApi, stores);
  zoom.initStore(docApi, stores);
  lhpStore.initStore(docApi, stores);
  documentViewStore.initStore(docApi, stores);
  searchStore.initStore(docApi, stores);
  hudStore.initStore(docApi, stores);
  accessibilityStore.initStore(docApi, stores);
  overlayDropinStore.initStore(docApi, stores);
  appUiState.initStore(docApi, stores);
  contextMenuStore.initStore(docApi, stores);
  dcapiStore.initStore(docApi, stores);
  previewStore.initStore(docApi, stores);
  bookmarkStore.initStore(docApi, stores);
  digSigStore.initStore(docApi, stores);
  topbarStore.initStore(docApi, stores);
  appStore.initStore(docApi, stores);
  externalStore.initStore(docApi, stores);
  thumbnailStore.initStore(docApi, stores);
  quickToolsStore.initStore(docApi, stores);
  stickyToolsStore.initStore(docApi, stores);
  notificationViewStore.initStore(docApi, stores);
  liquidModeViewStore.initStore(docApi, stores);
  liquidModePersonalisationStore.initStore(docApi, stores);
  highContrastStore.initStore(docApi, stores);
  pageThemeSwitcherStore.initStore(docApi, stores);
  readAloudStore.initStore(docApi, stores);
  caretModeStore.initStore(docApi, stores);
  // stores in preview-utils
  touchSelStore.initStore(stores);
  commonAppStore.init(commonCodeApis); // TODO: figure out a way to make this consistent with others
  pageHighlightsStore.initStore(stores);
  skinnyRailStore.initStore(docApi, stores);
  jsStore.initStore(stores);
  pageByPageStore.initStore(docApi, stores);
  organizeStore.initStore(docApi, stores);
  bottomBoard.initStore(stores);
  FABStore.initStore(docApi, stores);
  topPanelStore.initStore(docApi, stores);
};

export const resetStore = (switchPreview = false) => {
  documentStore.init();
  pagedViewState.init();
  zoom.init();
  lhpStore.init();
  documentViewStore.init();
  searchStore.init(switchPreview);
  hudStore.init();
  accessibilityStore.init();
  overlayDropinStore.init();
  appUiState.init();
  contextMenuStore.init();
  dcapiStore.init();
  previewStore.init();
  bookmarkStore.init();
  digSigStore.init();
  topbarStore.init();
  appStore.init(switchPreview);
  externalStore.init();
  thumbnailStore.init();
  quickToolsStore.init(switchPreview);
  stickyToolsStore.init();
  mipStore.init();
  notificationViewStore.init();
  liquidModeViewStore.init();
  liquidModePersonalisationStore.init();
  highContrastStore.init();
  pageThemeSwitcherStore.init();
  readAloudStore.init();
  caretModeStore.init();
  // stores in preview-utils
  touchSelStore.init();
  commonAppStore.init(commonCodeApis);
  pageHighlightsStore.init();
  cursorStore.init();
  skinnyRailStore.init(switchPreview);
  jsStore.init();
  pageByPageStore.init();
  organizeStore.init();
  bottomBoard.reset();
  FABStore.init();
  topPanelStore.init();
};

export {
  documentStore,
  pagedViewState,
  zoom,
  lhpStore,
  documentViewStore,
  searchStore,
  hudStore,
  accessibilityStore,
  overlayDropinStore,
  appUiState,
  contextMenuStore,
  dcapiStore,
  previewStore,
  bookmarkStore,
  digSigStore,
  topbarStore,
  appStore,
  externalStore,
  thumbnailStore,
  commonAppStore,
  touchSelStore,
  pageHighlightsStore,
  quickToolsStore,
  stickyToolsStore,
  notificationViewStore,
  liquidModeViewStore,
  liquidModePersonalisationStore,
  mipStore,
  highContrastStore,
  pageThemeSwitcherStore,
  readAloudStore,
  skinnyRailStore,
  jsStore,
  caretModeStore,
  cursorStore,
  pageByPageStore,
  organizeStore,
  bottomBoard,
  FABStore,
  topPanelStore
};
